import { useState } from 'react';
import { useRemoveCreatorFromPoolMutation } from '@round/creatorbase-helpers/api-hooks';

export type RemovePoolCreatorsResult = {
    failedAccountIds: number[];
};

/**
 * Removes a set of platform accounts from a pool, one request per account. Accounts are independent, so
 * one failing leaves the rest removed; the ids that failed are returned so the caller can report them.
 *
 * The mutation invalidates the pool-filtered creator-explore lists, so the pool table drops the removed
 * rows on its next refetch without any work here.
 */
export function useRemovePoolCreators(poolId: number | null | undefined) {
    const { removeAsync } = useRemoveCreatorFromPoolMutation(poolId);
    // A batch is several concurrent mutations, so the mutation's own `isPending` can't stand in for
    // "the whole batch is still running"; track that here instead.
    const [isRemoving, setIsRemoving] = useState(false);

    const removeAccounts = async (accountIds: number[]): Promise<RemovePoolCreatorsResult> => {
        if (!accountIds.length) {
            return { failedAccountIds: [] };
        }

        setIsRemoving(true);
        try {
            const results = await Promise.allSettled(
                accountIds.map((clientInfluencerAccountId) => removeAsync({ clientInfluencerAccountId }))
            );

            return {
                failedAccountIds: accountIds.filter((_, index) => results[index].status === 'rejected'),
            };
        } finally {
            setIsRemoving(false);
        }
    };

    return { removeAccounts, isRemoving };
}
